import { ImportModal } from "@etframework/ui.components";
import { useQueryClient } from "@tanstack/react-query";
import { saveAs } from "file-saver";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import importFileService from "../../data/api/importFileService";
import { useImportTemplates } from "../../data/mutations/importFileMutations";
import { useImportBatch } from "../../data/queries/importFileQueries";
import { useTaskType } from "../../data/queries/taskTypeQueries";
import useEvent from "../../hooks/useEvent";
import useModalEvent from "../../hooks/useModalEvent";

export const ImportTasksTemplatesModal = () => {
  const { t, i18n } = useTranslation(["ImportTaskTemplatesModal"]);
  const importModalRef = useRef();
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const { Events, toggleEvent } = useEvent();
  const [batchId, setBatchId] = useState(null);

  const [taskTypeId, setTaskTypeId] = useState(null);
  const { getTaskTemplateExcelTemplate } = importFileService();
  const { mutate: importFile } = useImportTemplates();
  const { data: importBatch, refetch: refetchImportBatch } =
    useImportBatch(batchId);
  const { data: taskType } = useTaskType(taskTypeId);

  const queryClient = useQueryClient();

  const downloadTemplate = async () => {
    const response = await getTaskTemplateExcelTemplate();
    const blob = new Blob([response.data]);
    saveAs(blob, response.filename);
  };

  useRegisterEvent(ModalEvents.ImportTemplates, (e) => {
    if (e?.detail?.step) {
      if (e?.detail?.step == 2) {
        setTimeout(() => {
          importModalRef?.current
            ?.getStepperRef()
            .getButtonsRef()
            .setContinueText(t("ReturnToUpload"));
          importModalRef?.current
            ?.getStepperRef()
            .getButtonsRef()
            .setBackText(t("Cancel"));
        });
      }
      importModalRef?.current?.getStepperRef()?.goToStep(e?.detail?.step);
      setTimeout(() => {
        importModalRef?.current.setErrorData(e?.detail?.errors);
      });
    } else {
      if (!e?.detail?.taskTypeId)
        console.error(
          "The import task templates modal was called without a task type ID"
        );

      setTaskTypeId(e.detail.taskTypeId);
    }

    importModalRef.current.open();
  });

  const refreshImportStatus = () => {
    const currentStep = importModalRef?.current?.getStepperRef()?.activeStep;

    if (batchId && currentStep === 1) {
      refetchImportBatch();

      if (importBatch?.Status === 2) {
        importModalRef?.current?.getStepperRef()?.goToStep(2);

        setTimeout(() => {
          setTimeout(() => {
            importModalRef?.current?.getStepperRef()?.setShowButtons(true);

            setTimeout(() => {
              importModalRef?.current
                ?.getStepperRef()
                ?.getButtonsRef()
                ?.setContinueText(t("ReturnToUpload"));

              importModalRef?.current
                ?.getStepperRef()
                ?.getButtonsRef()
                ?.setBackText(t("Cancel"));
            });
          });

          importModalRef?.current?.setErrorData(importBatch?.Errors);
        });

        setBatchId(null);
      } else if (importBatch?.Status === 3) {
        importModalRef?.current?.getStepperRef()?.goToStep(3);

        setBatchId(null);
        toggleEvent(Events.RefreshTaskTemplateGrid);
        queryClient.invalidateQueries(["get-tags"]);
        queryClient.invalidateQueries(["get-task-templates"], taskTypeId);
      }

      toggleEvent(Events.RefreshImportLogOverlay);
    }
  };

  useEffect(() => {
    /* Button text doesn't automatically change when changing the language */
    const buttonsRef = importModalRef.current.getStepperRef().getButtonsRef();
    buttonsRef.setCreateText(t("Create"));
    buttonsRef.setBackText(t("Cancel"));
    buttonsRef.setContinueText(t("Continue"));
  }, [i18n.language]);

  const header = (
    <div className="space-y-4">
      <p className="text-center fw-bold">
        {t("Title", { name: taskType?.Title })}
      </p>
      <p className="text-center">{t("Instructions")}</p>
      <div className="d-flex justify-content-center">
        <a
          href="#"
          onClick={downloadTemplate}
          className="text-center"
          id={ModalEvents.ImportTemplates.Id + "_download-template-link"}
        >
          {t("DownloadTemplate")}
        </a>
      </div>
    </div>
  );
  return (
    <ImportModal
      header={header}
      ref={importModalRef}
      id={ModalEvents.ImportTemplates.Id}
      importTitle={t("Title")}
      lang={i18n.language}
      importMultiple={false}
      continueText={t("Continue")}
      backText={t("Cancel")}
      createText={t("Create")}
      uploadFiles={(file) =>
        new Promise((resolve, reject) => {
          importFile(
            { taskTypeId: taskTypeId, file },
            {
              onSuccess: (response) => {
                if (response.Success) {
                  setBatchId(response.Id);
                  toggleEvent(Events.RefreshImportLogOverlay);
                  resolve(response);
                } else {
                  setTimeout(() => {
                    importModalRef?.current
                      ?.getUploaderErrorRef()
                      .setError(null);

                    importModalRef?.current
                      ?.getStepperRef()
                      ?.setShowButtons(true);
                    importModalRef?.current?.setErrorMessage(response.Message);
                  });
                  reject(response);
                }
              },
              onError: (err) => {
                console.error(err);
                reject(err);
              }
            }
          );
        })
      }
      onRefreshStatus={refreshImportStatus}
    />
  );
};
